import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, keys } from "@shared/api";
import type { InboxItem, Message, ReminderItem, Uuid } from "@shared/api";
import { Hint } from "@shared/ui/bits";
import { Popover, PopoverAnchor, PopoverContent } from "@shared/ui/popover";
import { IconCheck, IconClose, IconInbox, IconTimer } from "@shared/ui/icons";
import { Menu, type MenuItem } from "@shared/ui/Menu";
import { ReminderDialog, reminderChoices, reminderNote } from "@features/message-reminder";
import { directName, isGroup } from "@features/group-dm";
import { plural } from "@shared/lib/format";
import { fromPublic, Tile } from "@entities/user";
import { ServerTile } from "@entities/server";
import { useStore } from "@features/chat";
import { isCall } from "@entities/channel";

type Tab = "unread" | "mentions" | "saved" | "reminders";

const TABS: { value: Tab; label: string }[] = [
  { value: "unread", label: "Непрочитанные" },
  { value: "mentions", label: "Упоминания" },
  { value: "saved", label: "Закладки" },
  { value: "reminders", label: "Напоминания" },
];

const TAB_KEY = "airel.inbox-tab";

/** Последняя открытая вкладка — как в Discord, «Почта» открывается на ней. */
function readTab(): Tab {
  try {
    const value = localStorage.getItem(TAB_KEY);
    return value === "unread" || value === "mentions" || value === "saved" || value === "reminders"
      ? value
      : "unread";
  } catch {
    return "unread";
  }
}

function writeTab(tab: Tab) {
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // Хранилище закрыто — откроется на первой вкладке.
  }
}

/** Сколько новых сообщений канала показать в карточке «Непрочитанных». */
const UNREAD_SHOWN = 3;

/**
 * «Почта» — как в Discord: окно прямо под значком в полосе окна, а не посреди
 * экрана. Вкладки — «Непрочитанные» (каналы и переписки с новым, у каждой —
 * «прочитано»), «Упоминания» (где назвали по имени или ответили, свежие
 * сверху) и «Закладки» (свои сохранённые сообщения). Каждая запись —
 * карточка: значок сервера, «#канал» и «Сервер › Категория», ниже сообщения
 * целиком, как в ленте (`renderMessage`).
 *
 * Списки спрашиваются при каждом открытии: упоминания приходят постоянно, а
 * открывают окно редко — держать его свежим в фоне незачем.
 */
export function Inbox({
  renderMessage,
  onOpen,
  onOpenChannel,
  onClose,
}: {
  /** Сообщение целиком — его рисует лента (`MessagePreview`). */
  renderMessage: (message: Message) => ReactNode;
  onOpen: (item: InboxItem) => void;
  /** Открыть канал (`serverId` — `null` у переписки) — из «Непрочитанных». */
  onOpenChannel: (serverId: Uuid | null, channelId: Uuid) => void;
  onClose: () => void;
}) {
  const store = useStore();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>(readTab);

  // Окно стоит под значком «Почты» в полосе окна; без полосы — у правого верхнего угла.
  const anchor = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () =>
          document.querySelector("[data-inbox-anchor]")?.getBoundingClientRect() ??
          new DOMRect(window.innerWidth - 24, 46, 0, 0),
      },
    }),
    [],
  );

  // Каналы с новым — в самой ленте или в ветках, за которыми следят.
  const unreadChannels = [
    ...new Set([
      ...Object.keys(store.unread).filter((id) => (store.unread[id] ?? 0) > 0),
      ...Object.keys(store.threadUnread).filter((id) => (store.threadUnread[id] ?? 0) > 0),
    ]),
  ];
  const mentions = useQuery({ queryKey: keys.inbox(), queryFn: () => api.users.mentions(), staleTime: 0 });
  const saved = useQuery({
    queryKey: keys.saved(),
    queryFn: () => api.users.saved(),
    staleTime: 0,
    enabled: tab === "saved",
  });
  // Число сработавших — в подписи вкладки, как «Напоминания (1)» в Discord: спрашивается всегда.
  const reminders = useQuery({ queryKey: keys.reminders(), queryFn: () => api.users.reminders(), staleTime: 0 });
  const fired = (reminders.data ?? []).filter((item) => item.fired).length;
  /** Где открыто «Отложить» и для какого сообщения. */
  const [snoozing, setSnoozing] = useState<{ id: Uuid; at: { x: number; y: number } } | null>(null);
  const [timing, setTiming] = useState<Uuid | null>(null);

  /*
   * У карточки — категория и значок сервера, а их знает только состояние
   * сервера. Сервер, куда ещё не заходили, догружается — по разу на сервер.
   */
  const servers = [
    ...unreadChannels.map((id) => store.serverOf(id)),
    ...(mentions.data ?? []).map((item) => item.server_id),
    ...(saved.data ?? []).map((item) => item.server_id),
    ...(reminders.data ?? []).map((item) => item.server_id),
  ];
  useEffect(() => {
    for (const serverId of new Set(servers)) {
      if (serverId && !store.states[serverId]) void store.loadServer(serverId).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servers.join()]);

  /** Где канал: значок, «#имя» и «Сервер › Категория» — как в шапке карточки Discord. */
  function placeOf(channelId: Uuid, serverId: Uuid | null, fallbackName?: string | null) {
    if (serverId) {
      const server = store.servers.find((item) => item.id === serverId);
      const state = store.states[serverId];
      const channel = state?.channels.find((item) => item.id === channelId);
      const category = channel?.category_id
        ? state?.categories.find((item) => item.id === channel.category_id)?.name
        : null;
      const name = channel?.name ?? fallbackName ?? "канал";
      return {
        icon: <ServerTile id={serverId} name={server?.name ?? "сервер"} iconId={server?.icon_id} size={32} />,
        title: isCall(channel?.channel_type ?? "text") ? name : `#${name}`,
        where: [server?.name ?? "сервер", category].filter(Boolean).join(" › "),
      };
    }
    const direct = store.directs.find((item) => item.channel_id === channelId);
    const title = direct ? directName(direct) : "Личные сообщения";
    return {
      icon:
        direct && !isGroup(direct) ? (
          <Tile person={fromPublic(direct.recipient)} size={32} mark={null} />
        ) : (
          <ServerTile id={channelId} name={title} size={32} />
        ),
      title,
      where: "Личные сообщения",
    };
  }

  async function unsave(messageId: Uuid) {
    await api.users.unsave(messageId).catch(() => {});
    await queryClient.invalidateQueries({ queryKey: keys.saved() });
  }

  async function remind(messageId: Uuid, at: Date) {
    try {
      await api.users.remind(messageId, at);
    } finally {
      await queryClient.invalidateQueries({ queryKey: keys.reminders() });
    }
  }

  async function forget(messageId: Uuid) {
    await api.users.forget(messageId).catch(() => {});
    await queryClient.invalidateQueries({ queryKey: keys.reminders() });
  }

  const snoozeItems = (messageId: Uuid): MenuItem[] => [
    ...reminderChoices(new Date()).map((choice) => ({
      label: choice.label,
      onPick: () => void remind(messageId, choice.at).catch(() => {}),
    })),
    // После закрытия меню: окно и уходящее меню делили бы фокус.
    { label: "Другое…", onPick: () => setTimeout(() => setTiming(messageId)) },
  ];

  const pick = (next: Tab) => {
    setTab(next);
    writeTab(next);
  };

  const list: { isPending: boolean; data?: (InboxItem | ReminderItem)[] } =
    tab === "saved" ? saved : tab === "reminders" ? reminders : mentions;

  return (
    <Popover open onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor virtualRef={anchor} />
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          // Щелчок по самому значку закрывает окно им же — иначе оно закрылось бы и открылось снова.
          if ((event.target as HTMLElement | null)?.closest?.("[data-inbox-anchor]")) event.preventDefault();
        }}
      >
        <div className="inbox-pop glass-host">
          <header className="inbox-head">
            <IconInbox size={20} />
            <span className="inbox-title">Почта</span>
            {tab === "unread" && unreadChannels.length > 0 && (
              <Hint label="Отметить всё прочитанным" side="bottom">
                <button
                  type="button"
                  className="inbox-head-btn"
                  aria-label="Отметить всё прочитанным"
                  onClick={() => unreadChannels.forEach((id) => store.markRead(id))}
                >
                  <IconCheck size={18} />
                </button>
              </Hint>
            )}
          </header>

          <nav className="inbox-tabs" role="tablist" aria-label="Что показать">
            {TABS.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={tab === item.value}
                className={`inbox-tab ${tab === item.value ? "on" : ""}`}
                onClick={() => pick(item.value)}
              >
                {item.value === "reminders" && fired > 0 ? `${item.label} (${fired})` : item.label}
              </button>
            ))}
          </nav>

          <div className="inbox-body">
            {tab === "unread" ? (
              unreadChannels.length === 0 ? (
                <div className="inbox-empty">Всё прочитано — новых сообщений нет.</div>
              ) : (
                unreadChannels.map((channelId) => {
                  const serverId = store.serverOf(channelId) ?? null;
                  return (
                    <UnreadCard
                      key={channelId}
                      place={placeOf(channelId, serverId)}
                      channelId={channelId}
                      count={store.unread[channelId] ?? 0}
                      mentions={store.mentions[channelId] ?? 0}
                      threads={store.threadUnread[channelId] ?? 0}
                      renderMessage={renderMessage}
                      onOpen={() => onOpenChannel(serverId, channelId)}
                      onRead={() => store.markRead(channelId)}
                    />
                  );
                })
              )
            ) : list.isPending ? null : !list.data?.length ? (
              <div className="inbox-empty">
                {tab === "saved"
                  ? "Сохраните сообщение из его меню — и оно появится здесь."
                  : tab === "reminders"
                    ? "Попросите напомнить о сообщении из его меню — «Создать напоминание»."
                    : "Здесь появятся сообщения, где вас назвали по имени или ответили вам."}
              </div>
            ) : (
              list.data.map((item) => (
                <article key={item.message.id} className="inbox-card">
                  <CardHead place={placeOf(item.message.channel_id, item.server_id, item.channel_name)}>
                    <button type="button" className="inbox-go" onClick={() => onOpen(item)}>
                      Перейти
                    </button>
                    {"remind_at" in item && (
                      <>
                        <Hint label="Отложить напоминание" side="bottom">
                          <button
                            type="button"
                            className="inbox-card-btn"
                            aria-label="Отложить напоминание"
                            onClick={(event) => {
                              const box = event.currentTarget.getBoundingClientRect();
                              setSnoozing({ id: item.message.id, at: { x: box.left, y: box.bottom + 4 } });
                            }}
                          >
                            <IconTimer size={15} />
                          </button>
                        </Hint>
                        <Hint label="Удалить напоминание" side="bottom">
                          <button
                            type="button"
                            className="inbox-card-btn"
                            aria-label="Удалить напоминание"
                            onClick={() => void forget(item.message.id)}
                          >
                            <IconClose size={15} />
                          </button>
                        </Hint>
                      </>
                    )}
                    {tab === "saved" && (
                      <Hint label="Убрать из закладок" side="bottom">
                        <button
                          type="button"
                          className="inbox-card-btn"
                          aria-label="Убрать из закладок"
                          onClick={() => void unsave(item.message.id)}
                        >
                          <IconClose size={15} />
                        </button>
                      </Hint>
                    )}
                  </CardHead>
                  {"remind_at" in item && (
                    <div className={`inbox-reminder ${item.fired ? "fired" : ""}`}>
                      <IconTimer size={14} />
                      {reminderNote(item.remind_at, item.fired)}
                    </div>
                  )}
                  <div className="inbox-card-body">{renderMessage(item.message)}</div>
                </article>
              ))
            )}
          </div>
        </div>

        {snoozing && (
          <Menu
            title="ОТЛОЖИТЬ"
            at={snoozing.at}
            items={snoozeItems(snoozing.id)}
            onClose={() => setSnoozing(null)}
          />
        )}
        {timing && (
          <ReminderDialog onSave={(at) => remind(timing, at)} onClose={() => setTiming(null)} />
        )}
      </PopoverContent>
    </Popover>
  );
}

interface Place {
  icon: ReactNode;
  title: string;
  where: string;
}

/** Шапка карточки — значок, «#канал», «Сервер › Категория» и действия справа. */
function CardHead({ place, children }: { place: Place; children?: ReactNode }) {
  return (
    <header className="inbox-card-head">
      {place.icon}
      <span className="inbox-card-place">
        <b className="ellipsis">{place.title}</b>
        <span className="ellipsis">{place.where}</span>
      </span>
      <span className="inbox-card-actions">{children}</span>
    </header>
  );
}

/**
 * Канал с новым — как в «Непрочитанных» Discord: последние новые сообщения
 * целиком, ниже — сколько ещё, сверху — «прочитано». Новое только в ветках
 * отмечают в самих ветках: канал и так прочитан.
 */
function UnreadCard({
  place,
  channelId,
  count,
  mentions,
  threads,
  renderMessage,
  onOpen,
  onRead,
}: {
  place: Place;
  channelId: Uuid;
  count: number;
  mentions: number;
  threads: number;
  renderMessage: (message: Message) => ReactNode;
  onOpen: () => void;
  onRead: () => void;
}) {
  const shown = Math.min(count, UNREAD_SHOWN);
  const { data } = useQuery({
    queryKey: ["inbox-unread", channelId, shown],
    queryFn: () => api.messages.list(channelId, { limit: shown }),
    enabled: shown > 0,
    staleTime: 0,
  });
  const messages = [...(data ?? [])].sort((a, b) => a.created.localeCompare(b.created));
  const more = count - messages.length;

  return (
    <article className="inbox-card">
      <CardHead place={place}>
        {mentions > 0 && <span className="badge">@{mentions}</span>}
        <button type="button" className="inbox-go" onClick={onOpen}>
          Перейти
        </button>
        {count > 0 && (
          <Hint label="Отметить прочитанным" side="bottom">
            <button type="button" className="inbox-card-btn" aria-label="Отметить прочитанным" onClick={onRead}>
              <IconCheck size={15} />
            </button>
          </Hint>
        )}
      </CardHead>
      {messages.length > 0 && (
        <div className="inbox-card-body">
          {messages.map((message) => (
            <div key={message.id}>{renderMessage(message)}</div>
          ))}
        </div>
      )}
      {(more > 0 || threads > 0) && (
        <button type="button" className="inbox-more" onClick={onOpen}>
          {[
            more > 0 ? `Ещё ${more >= 100 ? "100+" : more} ${plural(more, "новое", "новых", "новых")}` : null,
            threads > 0 ? `веток с новым: ${threads}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </button>
      )}
    </article>
  );
}
